import { useMutation, useQuery } from 'react-query';
import React, { Fragment, useState } from 'react';
import classNames from 'classnames/bind';
import BigNumber from 'bignumber.js';
import apiClient from '../../helpers/apiClient';
import useSettings from '../../components/Game/hooks/useSettings';
import Modal from '../../components/Modal/Modal';
import styles from './styles.scss';
import useAppStore from '../../useAppStore';
import { useGameStore } from '../../components/Game/hooks/useGameStore';
import useAffiliateScript from './useAffiliateScript';

const cx = classNames.bind(styles);

async function sendDeposit(amount) {
  const { data: transactionData } = await apiClient().post(
    '/v1/deposit/deposit',
    {
      asset: 'BSV',
      amount,
    },
    { timeout: 1000 * 60 }
  );
  return { transactionData, amount };
}
async function sendBet(receiver, amount, data, asset) {
  const { data: transactionData } = await apiClient().post(
    '/v1/wallets/send',
    {
      asset,
      receiver,
      amount,
      data,
    },
    { timeout: 1000 * 60 }
  );
  return { transactionData, amount };
}
async function getTopupUri() {
  return (await apiClient().get('/v1/wallets/topup')).data.uri;
}
// this hook is used for non money button bets
export default function useBet(bettingData, onBetSuccess, isDeposit) {
  const selectedAsset = useAppStore(state => state.asset.selected);
  const affiliateScript = useAffiliateScript();
  const bettingScript = bettingData.script.replace('BSV', selectedAsset) + affiliateScript;
  const betAmount = bettingData.multiplier
    ? bettingData.amount.multipliedBy(bettingData.multiplier).toFixed()
    : bettingData.amount;
  const [lowBalance, setLowBalance] = useState(false);
  const [spendLimit, setSpendLimit] = useState(false);
  const [lowPgpBalance, setLowPgpBalance] = useState(false);
  const { isLoading: settingsLoading, address: receiver } = useSettings({ enabled: !isDeposit });
  const { mutate: placeBet, isLoading: isSendingBet, error } = useMutation(
    isDeposit ? () => sendDeposit(betAmount) : () => sendBet(receiver, betAmount, bettingScript, selectedAsset),
    {
      onSuccess: ({ transactionData, amount }) => {
        const { asset } = transactionData;
        const paymentAmount = new BigNumber(amount);

        if (asset === 'PGP') {
          useAppStore.getState().setAssetBalance(currentBalance => ({
            PGP: currentBalance.PGP.minus(paymentAmount),
          }));
        }
        if (isDeposit) {
          useGameStore.getState().setBettingDisabled(false);
        }
        onBetSuccess(transactionData);
      },
      onError: err => {
        useGameStore.getState().setBettingDisabled(false);
        if (err.code === '__low_balance__') {
          if (selectedAsset === 'PGP') {
            setLowPgpBalance(true);
          } else {
            setLowBalance(true);
          }
        } else if (err.code === '__spend_limit__') {
          setSpendLimit(true);
        }
      },
    }
  );
  const { data: topupUri } = useQuery('topup', getTopupUri, {
    retry: false,
    enabled: !!error && (error.code === '__low_balance__' || error.code === '__spend_limit__'),
  });
  const isLoading = settingsLoading || isSendingBet;
  const errorModals = (
    <Fragment>
      <Modal isOpen={lowBalance} header="Low Balance" onClose={() => setLowBalance(false)}>
        <div className={cx('modal-body')}>
          <p>Your balance is too low to make this payment</p>
          <a href={topupUri} target="_blank" rel="noreferrer">
            Add Balance
          </a>
        </div>
      </Modal>
      <Modal isOpen={spendLimit} header="Spend Limit" onClose={() => setSpendLimit(false)}>
        <div className={cx('modal-body')}>
          <p>Your wallet spend limit is too low to make this payment. Please top up and try again.</p>
          <a href={topupUri} target="_blank" rel="noreferrer">
            Adjust Spend Limit
          </a>
        </div>
      </Modal>
      <Modal isOpen={lowPgpBalance} header="Low balance" onClose={() => setLowPgpBalance(false)}>
        <div className={cx('modal-body')}>
          <p>Your Peergame Points balance is too low to make this payment</p>
        </div>
      </Modal>
    </Fragment>
  );
  return {
    placeBet,
    isLoading,
    error,
    errorModals,
  };
}
